import React, { Suspense } from 'react';
import { createBrowserRouter, Routes, Route } from 'react-router-dom';
import { routeConfig } from './navigation-service';
import { LoaderOverlay } from '@/components/common/LoaderOverlay';
import ErrorPageRoute from '@/pages/ErrorPage';

// Lazy load page components for better initial load performance
const WithdrawPage = React.lazy(() => import('@/pages/WithdrawPage'));
const WithdrawalConfirmation = React.lazy(() => import('@/pages/WithdrawalConfirmation'));
const ProtectedRoute = React.lazy(() => import('@/components/auth/ProtectedRoute').then(m => ({ default: m.ProtectedRoute })));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));

/**
 * Preload components in the background after initial load
 * This ensures subsequent navigation is instant
 */
export const preloadComponents = () => {
  // Preload all route components in the background
  // These will be cached by the browser for instant subsequent loads
  setTimeout(() => {
    // Main route components
    import('@/pages/WithdrawPage');
    import('@/pages/WithdrawalConfirmation');
    import('@/components/auth/ProtectedRoute');
    import('@/pages/AuthCallback');
    
    // Heavy child components within pages
    import('@/pages/WithdrawalAccordion/WithdrawalAccordion');
    import('@/pages/PreviousWithdrawals/PreviousWithdrawals');
    
    // Withdrawal method components
    import('@/pages/WithdrawalMethods');
    
    // Modal components
    import('@/components/BankOfflineModal');
    import('@/components/VoucherOfflineModal');
  }, 100);
};

/**
 * Application routing configuration
 * Centralizes all route definitions for better maintainability
 */
export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoaderOverlay />}>
      <Routes>
        <Route path={routeConfig.withdraw} element={<WithdrawPage />} />
        <Route path={routeConfig.withdrawIframe} element={<WithdrawPage />} />
        <Route path={routeConfig.withdrawalConfirmation} element={<WithdrawalConfirmation />} />
      </Routes>
    </Suspense>
  );
};

/**
 * Router configuration for RouterProvider
 */
export const router = createBrowserRouter([
  {
    path: routeConfig.authCallback,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <AuthCallback />
      </Suspense>
    )
  },
  {
    path: routeConfig.withdraw,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <WithdrawPage />
      </Suspense>
    ),
  },
  {
    path: routeConfig.withdrawIframe,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <ProtectedRoute>
          <WithdrawPage />
        </ProtectedRoute>
      </Suspense>
    )
  },
  {
    path: routeConfig.withdrawalConfirmation,
    element: (
      <Suspense fallback={<LoaderOverlay />}>
        <WithdrawalConfirmation />
      </Suspense>
    ),
  },
  {
    path: routeConfig.error,
    element: <ErrorPageRoute />,
  },
]);

/**
 * Route metadata for additional information
 */
export const routeMetadata = {
  [routeConfig.withdraw]: {
    title: 'Withdrawals',
    description: 'Withdrawal interface',
    requiresAuth: false,
  },
  [routeConfig.withdrawIframe]: {
    title: 'Withdrawals - Iframe',
    description: 'Withdrawal interface for iframe',
    requiresAuth: false,
  },
  [routeConfig.iframe]: {
    title: 'Iframe Container',
    description: 'Iframe wrapper page',
    requiresAuth: false,
  },
  [routeConfig.withdrawalConfirmation]: {
    title: 'Withdrawal Confirmation',
    description: 'Confirm withdrawal details',
    requiresAuth: false,
  },
  [routeConfig.error]: {
    title: 'Error',
    description: 'Error page',
    requiresAuth: false,
  },
} as const;